import { useEffect, useId, useMemo, useState } from 'react'

export type OverlayItem = {
  id: string,
  tags?: string[],
}

type OverlayItemInformation = OverlayItem & {
  zIndex: number,
  position: number,
  tagPositions: Record<string, number>,
}

type OverlayRegistryValue = {
  itemInformation: Record<string, OverlayItemInformation>,
  tagItemCounts: Record<string, number>,
  activeId: string | null,
}

type OverlayRegistryListenerCallback = (value: OverlayRegistryValue) => void

export class OverlayRegistry {
  private static instance: OverlayRegistry | null = null

  static getInstance(): OverlayRegistry {
    if (!OverlayRegistry.instance) {
      OverlayRegistry.instance = new OverlayRegistry()
    }
    return OverlayRegistry.instance
  }

  // The frontmost overlay is at the end of the array
  private overlayIds: string[] = []
  private overlayItems: Record<string, OverlayItem> = {}
  private listeners = new Set<OverlayRegistryListenerCallback>()

  register(item: OverlayItem) {
    this.overlayIds = this.overlayIds.filter(id => id !== item.id)
    this.overlayIds.push(item.id)
    this.overlayItems[item.id] = item
    this.notify()
    return () => {
      this.overlayIds = this.overlayIds.filter(id => id !== item.id)
      delete this.overlayItems[item.id]
      this.notify()
    }
  }

  update(item: OverlayItem) {
    this.overlayItems[item.id] = item
    this.notify()
  }

  addListener(callback: OverlayRegistryListenerCallback) {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }

  private notify() {
    const itemInformation: Record<string, OverlayItemInformation> = {}
    const ids = [...this.overlayIds]
    const startZIndex = 100
    const tagCount: Record<string, number> = {}
    for (let index = ids.length - 1; index >= 0; index--) {
      const id = ids[index]
      const item = this.overlayItems[id]
      itemInformation[id] = {
        ...item,
        position: index,
        tagPositions: {},
        zIndex: startZIndex + index,
      }
      for(const tag of item.tags ?? []) {
        const count = tagCount[tag] ?? 0
        const nextPosition = count
        tagCount[tag] = count + 1
        itemInformation[id].tagPositions[tag] = nextPosition
      }
    }
    for (const callback of this.listeners) {
      callback({ activeId: ids[ids.length - 1] ?? null, itemInformation, tagItemCounts: tagCount })
    }
  }
}

export type UseOverlayRegistryProps = Partial<OverlayItem> & {
    isActive?: boolean,
    /** Tags cannot change on every render, thus make sure they are wrapped in a useMemo or similar */
    tags?: string[],
}

export type UseOverlayRegistryResult = {
    isInFront: boolean,
    zIndex?: number,
    position?: number,
    tagPositions?: Record<string, number>,
    tagItemCounts: Record<string, number>,
}

export const useOverlayRegistry = (props: UseOverlayRegistryProps = {}): UseOverlayRegistryResult => {
  const generatedId = useId()
  const item: OverlayItem = useMemo(() => ({
    id: props.id ?? generatedId,
    tags: props.tags,
  }), [props.id, generatedId, props.tags])
  const [value, setValue] = useState<OverlayRegistryValue>({
    activeId: null,
    itemInformation: {},
    tagItemCounts: {}
  })
  const registry = useMemo(() => OverlayRegistry.getInstance(), [])

  useEffect(() => {
    if(!props.isActive) {
      return
    }
    function callback(value: OverlayRegistryValue) {
      setValue(value)
    }

    const removeListener = registry.addListener(callback)
    const unregister = registry.register(item)
    return () => {
      removeListener()
      unregister()
      setValue({
        activeId: null,
        itemInformation: {},
        tagItemCounts: {}
      })
    }
  }, [props.isActive, item, registry])

  const itemInformation = value.itemInformation[item.id]

  return {
    isInFront: value.activeId === item.id,
    zIndex: itemInformation?.zIndex,
    position: itemInformation?.position,
    tagPositions: itemInformation?.tagPositions,
    tagItemCounts: value.tagItemCounts,
  }
}